import { fmtDateTime } from "@/lib/time";
import React, { useCallback, useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Bell, CheckCircle2, XCircle, Info, BellRing, ChevronLeft, CalendarPlus } from "lucide-react";
import { useAuth } from "@/contexts/AuthContext";
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetTrigger } from "@/components/ui-kit";
import { api } from "@/lib/api";
import { LIVE_MS, pingLive } from "@/lib/live";
import { usePrefs } from "@/contexts/PrefsContext";
import type { AppNotification } from "@/types";
import { PushToggle } from "@/components/PushToggle";

const iconFor = (kind: string) =>
  kind === "booking_reminder" ? (
    <BellRing className="h-5 w-5 text-secondary" />
  ) : kind.includes("cancel") || kind.includes("rejected") ? (
    <XCircle className="h-5 w-5 text-primary" />
  ) : kind.includes("confirm") || kind.includes("approved") ? (
    <CheckCircle2 className="h-5 w-5 text-emerald-500" />
  ) : (
    <Info className="h-5 w-5 text-secondary" />
  );

export const notifTarget = (n: AppNotification, role?: string): string => {
  if (n.link) return n.link;
  const k = n.kind || "";
  if (role === "customer") return n.booking_id ? `/app/bookings?ticket=${n.booking_id}` : k.startsWith("case") ? "/app/profile" : "/app/bookings";
  if (role === "venue") {
    if (n.booking_id) return "/venue/bookings";
    if (k.startsWith("payment") || k.includes("fee") || k.includes("balance")) return "/venue/finance";
    if (k.startsWith("event")) return "/venue/events";
    if (k.startsWith("account")) return "/venue/settings";
    return "/venue";
  }
  if (k.startsWith("case")) return "/admin/cases";
  if (k === "venue_registered") return "/admin/partners";
  if (k.includes("booking")) return "/admin/bookings";
  return "/admin";
};

const Item = ({ n, onOpen }: { n: AppNotification; onOpen: () => void }) => {
  const { t, lang } = usePrefs();
  return (
    <button type="button" data-testid={`notification-item-${n.notification_id}`} data-read={n.read ? "true" : "false"} onClick={onOpen}
      className={`w-full flex gap-3 p-3 rounded-2xl border text-start cursor-pointer transition-[background-color,border-color,opacity] hover:border-primary/50 active:scale-[0.99] ${n.read ? "bg-card opacity-70" : "bg-primary/10 border-primary/30 shadow-sm"}`}>
      <div className="pt-0.5 relative">
        {iconFor(n.kind)}
        {!n.read && <span className="absolute -top-0.5 -end-0.5 h-2.5 w-2.5 rounded-full bg-primary ring-2 ring-card" />}
      </div>
      <div className="space-y-1 flex-1 min-w-0">
        <div className="flex items-center gap-2">
          <p className={`text-sm flex-1 ${n.read ? "font-semibold" : "font-black"}`}>{lang === "ar" ? n.title_ar : n.title_en}</p>
          {!n.read && <span data-testid={`notification-new-${n.notification_id}`} className="rounded-full bg-primary text-primary-foreground px-2 py-0.5 text-[10px] font-bold">{t("notif.new")}</span>}
        </div>
        <p className="text-sm text-muted-foreground leading-relaxed">{lang === "ar" ? n.body_ar : n.body_en}</p>
        <p className="text-[11px] text-muted-foreground flex items-center justify-between gap-2">
          {fmtDateTime(n.created_at, lang)}
          {n.kind === "series_ending"
            ? <span data-testid={`notification-extend-${n.notification_id}`} className="inline-flex items-center gap-1 rounded-full bg-primary text-primary-foreground px-3 py-1 text-xs font-bold"><CalendarPlus className="h-3.5 w-3.5" />{t("notif.extend")}</span>
            : <span className="inline-flex items-center gap-0.5 text-primary font-bold">{t("notif.open")}<ChevronLeft className="h-3 w-3 ltr:rotate-180" /></span>}
        </p>
      </div>
    </button>
  );
};

export const NotificationsBell = () => {
  const { t, lang } = usePrefs();
  const [items, setItems] = useState<AppNotification[]>([]);
  const [open, setOpen] = useState(false);
  const [filter, setFilter] = useState<"all" | "unread">("all");
  const navigate = useNavigate();
  const { user } = useAuth();
  const role = user ? user.role : undefined;
  const unread = items.filter((n) => !n.read).length;
  const seen = useRef<Set<string> | null>(null);

  const openItem = useCallback((n: AppNotification) => {
    setOpen(false);
    if (!n.read) {
      setItems((xs) => xs.map((x) => (x.notification_id === n.notification_id ? { ...x, read: true } : x)));
      api.post(`/notifications/${n.notification_id}/read`).catch(() => {});
    }
    navigate(notifTarget(n, role));
  }, [navigate, role]);

  const load = useCallback(() => {
    api.get("/notifications").then((r) => {
      const list: AppNotification[] = r.data;
      const prev = seen.current;
      seen.current = new Set(list.map((n) => n.notification_id));
      setItems(list);
      if (!prev) return;
      const fresh = list.filter((n) => !n.read && !prev.has(n.notification_id));
      if (!fresh.length) return;
      pingLive(true);
      const n = fresh[0];
      toast(lang === "ar" ? n.title_ar : n.title_en, {
        description: fresh.length > 1 ? t("notif.more").replace("{n}", String(fresh.length - 1)) : (lang === "ar" ? n.body_ar : n.body_en),
        action: { label: t("notif.open"), onClick: () => openItem(n) },
      });
    }).catch(() => {});
  }, [lang, t, openItem]);

  useEffect(() => {
    load();
    const tickIt = () => { if (document.visibilityState === "visible") { load(); pingLive(); } };
    const id = setInterval(tickIt, LIVE_MS);
    document.addEventListener("visibilitychange", tickIt);
    window.addEventListener("wn:notify", load);
    return () => {
      clearInterval(id);
      document.removeEventListener("visibilitychange", tickIt);
      window.removeEventListener("wn:notify", load);
    };
  }, [load]);

  const markAll = async () => {
    await api.post("/notifications/read-all");
    load();
  };
  const shown = filter === "unread" ? items.filter((n) => !n.read) : items;

  return (
    <Sheet open={open} onOpenChange={(o: boolean) => { setOpen(o); if (o) load(); }}>
      <SheetTrigger asChild>
        <button
          data-testid="notifications-bell-button"
          className="relative h-10 w-10 grid place-items-center rounded-full border bg-card/70 backdrop-blur hover:bg-accent active:scale-95 transition-[transform,background-color]"
        >
          <Bell className={`h-4 w-4 ${unread ? "text-primary" : ""}`} />
          {unread > 0 && (
            <span data-testid="notifications-unread-count"
              className="absolute -top-1 -end-1 min-w-5 h-5 px-1 rounded-full bg-primary text-primary-foreground text-[11px] font-bold grid place-items-center">
              {unread}
            </span>
          )}
        </button>
      </SheetTrigger>
      <SheetContent side={lang === "ar" ? "left" : "right"} className="w-[88vw] sm:max-w-sm p-0" data-testid="notifications-panel">
        <SheetHeader className="p-5 border-b text-start space-y-3">
          <SheetTitle className="font-display text-xl">{t("notif.title")}</SheetTitle>
          <div className="flex items-center gap-2">
            {(["all", "unread"] as const).map((f) => (
              <button key={f} onClick={() => setFilter(f)} aria-pressed={filter === f} data-testid={`notifications-filter-${f}`}
                className={`h-8 px-3 rounded-full text-xs font-bold transition-[background-color,color] ${filter === f ? "bg-foreground text-background" : "bg-muted"}`}>
                {t(`notif.f.${f}`)}{f === "unread" && unread ? ` (${unread})` : ""}
              </button>
            ))}
            {unread > 0 && (
              <button data-testid="notifications-mark-read-button" onClick={markAll} className="ms-auto text-xs text-primary font-bold">
                {t("notif.markRead")}
              </button>
            )}
          </div>
        </SheetHeader>
        <div className="overflow-y-auto max-h-[calc(100vh-150px)] p-3 space-y-2">
          <PushToggle compact />
          {shown.length === 0 && <p data-testid="notifications-empty" className="text-center text-muted-foreground py-10">{t(filter === "unread" ? "notif.noUnread" : "notif.empty")}</p>}
          {shown.map((n) => <Item key={n.notification_id} n={n} onOpen={() => openItem(n)} />)}
        </div>
      </SheetContent>
    </Sheet>
  );
};
